import { useCallback, useRef, useState } from 'react';
import type { JSX } from 'react';
import InputDialog from '../InputDialog';
import ConfirmDialog from '../ConfirmDialog';

/**
 * The project's own replacement for window.prompt / window.confirm in Studio: a name asked in the
 * dark dialog (InputDialog) and a confirmation in the confirm sheet (ConfirmDialog), both awaited
 * like the system calls were. `askName` resolves to the trimmed text, or null when the dialog was
 * cancelled or left empty; `askConfirm` to true / false. Render `dialogs` once in the host.
 */
export interface AskDialogs {
  readonly askName: (title: string, initial?: string, opts?: AskNameOptions) => Promise<string | null>;
  readonly askConfirm: (title: string, description?: string, opts?: AskConfirmOptions) => Promise<boolean>;
  readonly dialogs: JSX.Element;
}

/** `maxLength`: the server's limit for the name — counted in the field, refused before sending. */
export interface AskNameOptions { readonly maxLength?: number }
/** `tone: 'danger'`: the confirmed action deletes something — a red button. */
export interface AskConfirmOptions { readonly tone?: 'default' | 'danger' }

type Pending =
  | { readonly kind: 'name'; readonly title: string; readonly initial: string; readonly maxLength?: number; readonly resolve: (value: string | null) => void }
  | { readonly kind: 'confirm'; readonly title: string; readonly description: string; readonly tone?: 'default' | 'danger'; readonly resolve: (value: boolean) => void };

export function useAskDialogs(): AskDialogs {
  const [pending, setPending] = useState<Pending | undefined>(undefined);
  // Resolve once: the Radix close and the button handler both fire on a confirm.
  const settled = useRef<boolean>(false);

  const askName = useCallback(
    (title: string, initial = '', opts: AskNameOptions = {}): Promise<string | null> =>
      new Promise((resolve) => {
        settled.current = false;
        setPending({ kind: 'name', title, initial, maxLength: opts.maxLength, resolve });
      }),
    [],
  );
  const askConfirm = useCallback(
    (title: string, description = '', opts: AskConfirmOptions = {}): Promise<boolean> =>
      new Promise((resolve) => {
        settled.current = false;
        setPending({ kind: 'confirm', title, description, tone: opts.tone, resolve });
      }),
    [],
  );

  const settle = (fn: () => void): void => {
    if (settled.current) return;
    settled.current = true;
    fn();
    setPending(undefined);
  };

  const dialogs = (
    <>
      <InputDialog
        open={pending?.kind === 'name'}
        onOpenChange={(open: boolean): void => {
          if (!open && pending?.kind === 'name') settle(() => pending.resolve(null));
        }}
        title={pending?.kind === 'name' ? pending.title : ''}
        defaultValue={pending?.kind === 'name' ? pending.initial : ''}
        maxLength={pending?.kind === 'name' ? pending.maxLength : undefined}
        onConfirm={(value: string): void => {
          if (pending?.kind !== 'name') return;
          const trimmed = value.trim();
          settle(() => pending.resolve(trimmed === '' ? null : trimmed));
        }}
        onCancel={(): void => {
          if (pending?.kind === 'name') settle(() => pending.resolve(null));
        }}
      />
      <ConfirmDialog
        open={pending?.kind === 'confirm'}
        onOpenChange={(open: boolean): void => {
          if (!open && pending?.kind === 'confirm') settle(() => pending.resolve(false));
        }}
        title={pending?.kind === 'confirm' ? pending.title : ''}
        description={pending?.kind === 'confirm' ? pending.description : ''}
        tone={pending?.kind === 'confirm' ? pending.tone : undefined}
        onConfirm={(): void => {
          if (pending?.kind === 'confirm') settle(() => pending.resolve(true));
        }}
        onCancel={(): void => {
          if (pending?.kind === 'confirm') settle(() => pending.resolve(false));
        }}
      />
    </>
  );

  return { askName, askConfirm, dialogs };
}
